import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretLeft, CaretRight, Trophy, Sparkle, Warning, ChatCircleText, SealCheck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { getLevelLabel, LEVEL_MIN, LEVEL_MAX } from '../utils/level';

// Official Padelarena tactical & technical test (June 2026). Scoring happens
// server-side; `multi: true` renders checkboxes (several answers possible).
const QUIZ = {
  de: [
    { multi: true, title: 'Warum ist es ein taktischer Vorteil, wenn ein Linkshänder auf der rechten Seite spielt?', options: [
      'Weil die offiziellen FIP-Regeln Linkshänder zwingend auf die rechte Seite verweisen.',
      'Beide Spieler decken mit der Vorhand die Platzmitte ab, wohin die meisten Bälle fliegen.',
      'Beide Spieler können Angriffsschläge/Smashs aus der Mitte mit dem dominanten Arm schlagen.',
      'Weil Linkshänder links regeltechnisch keine Bälle von der Wand spielen dürfen.',
    ] },
    { multi: false, title: 'Wann wird im Padel ein Lob (Lob-Schlag) gespielt?', options: [
      'Ausschliesslich dann, wenn man unter extremem Druck in der Verteidigung steht.',
      'Ausschliesslich dann, wenn man die totale Ballkontrolle und keinen Gegnerdruck hat.',
      'Beides: defensiv (zur Zeiterholung) und offensiv (Gegner zu nah am Netz, um es zu erobern).',
      'Niemals, weil jeder Lob dem Gegner einen einfachen Smash ermöglicht.',
    ] },
    { multi: true, title: 'Was ist eine „Chiquita“ und wie verhält man sich danach taktisch?', options: [
      'Ein langsamer, weicher Ball tief vor die Füsse der gegnerischen Netzspieler.',
      'Zwingt den Gegner zum Volley von unten nach oben; erzeugt einen attackierbaren Folgeball.',
      'Nach präzisem Ball rückt man gemeinsam mit dem Partner vor, um das Netz zu fordern.',
      'Ein maximal harter, flacher Schlag direkt auf den Oberkörper des Gegners.',
    ] },
    { multi: true, title: 'Du stehst am Netz und spielst versehentlich einen kurzen, hohen Lob. Richtige Reaktion?', options: [
      'Am Netz stehen bleiben und vorrücken, um den Smash abzublocken.',
      'Sofort zügig nach hinten in die Grundlinien-Verteidigung weichen, um den Smash zu erwarten.',
      'Schlagwinkel des Gegners lesen und Abprall von der hinteren Glaswand verteidigen.',
      'Auf der T-Linie stehen bleiben und beobachten, wo der Ball aufkommt.',
    ] },
    { multi: true, title: 'Wie sollten Körperhaltung und Treffpunkt beim defensiven Grundlinien-Lob sein?', options: [
      'Treffpunkt leicht vor, aber nah am Körper; tiefer Schwerpunkt, Heben primär über Pendelbewegung aus Schulter/Arm ohne übermässige Körperstreckung.',
      'Treffpunkt vor dem Körper, Heben primär durch aktive Beugung und Streckung der Beine (Hochdrücken aus den Beinen).',
      'Treffpunkt weit hinter dem Körper rein aus schneller Handgelenksbeschleunigung.',
      'Treffpunkt weit über den Schultern im Rückwärtssprung mit beiden Beinen.',
    ] },
    { multi: false, title: 'Was ist der zuverlässigste Standard-Grundlinienschlag (Vorhand-Drive) im Padel?', options: [
      'Starker Topspin mit aggressiver Schlägerbeschleunigung wie beim Sandplatztennis.',
      'Kompakter, flacher Schlag oder leichter Slice für Kontrolle und flachen Glaswandabprall.',
      'Immer ein hoher, langsamer Slice-Ball in die diagonale Ecke.',
      'Aggressiver Sidespin-Schlag bei jedem Grundliniendrive.',
    ] },
    { multi: true, title: 'Wie wird eine offensive Wandverteidigung (Bajada de pared) ausgeführt?', options: [
      'Netzabstand der Gegner vor Schlag analysieren: Nah am Netz = Slice auf Füsse/Körper oder Chiquita; weiter hinten = flacher Schnellball durch die Mitte.',
      'Schläger früh über Schulterhöhe ansetzen, Ball höchsten Punkt nach Glas erreichen lassen und mit voller flacher Kraft nach unten schlagen.',
      'Mit Topspin nach hinten fallend schlagen, damit der Ball sehr hoch von der Wand springt.',
      'Auf Aufschlaglinie stehen bleiben und Ball vor der Wand spielen, um Wände zu meiden.',
    ] },
    { multi: false, title: 'Was ist nach dem eigenen Aufschlag die unmittelbare Priorität?', options: [
      'An der Grundlinie stehen bleiben und abwarten, ob der gegnerische Return schwach ist.',
      'Direkt nach Treffpunkt nach vorne aufrücken, um mit Partner Netzposition zu besetzen.',
      'Bis zur T-Linie laufen und dort pausieren, um den Punkt abzuwarten.',
      'Direkt mit dem Partner die Seite tauschen, während der Ball noch fliegt.',
    ] },
    { multi: true, title: 'Gegner spielen tiefen, präzisen Lob über deinen Kopf am Netz. Wie reagierst du?', options: [
      'Seitlich mit Kreuzschritten drehen; fehlt Balance für Überkopfschlag, früh rufen, synchron mit Partner rotieren und Wand verteidigen.',
      'Mit Blick zum Netz so schnell wie möglich rückwärts laufen bis zur Wand, vor Aufprall bremsen.',
      'Am Netz stehen bleiben und im Rückwärtssprung versuchen, Ball mit Schlägerspitze zu tippen.',
      'Am Netz bleiben, während der Partner alleine nach hinten sprintet.',
    ] },
    { multi: true, title: 'Was ist der technische und taktische Unterschied zwischen Bandeja und Víbora?', options: [
      'Bandeja dient Kontrolle, Sicherheit und Tiefe, um Netz ohne hohes Risiko zu halten.',
      'Víbora ist offensiver Überkopfschlag mit Sidespin, der Ball nach Glas flach und schnell hält.',
      'Bandeja mit offener Fläche vor/über Kopf; Víbora seitlich mit Pronation/Handgelenk gebürstet.',
      'Kein technischer Unterschied; Begriff hängt nur von Links- oder Rechtshändern ab.',
    ] },
  ],
  en: [
    { multi: true, title: 'Why is it a tactical advantage for a left-handed player to play on the right side?', options: [
      'Because official FIP rules strictly force left-handers to play on the right side.',
      'Both players have their forehand covering the middle, where most balls travel.',
      'Both players can execute overhead smashes/bandejas from the center with their dominant arm.',
      'Because left-handers are prohibited from returning side-wall balls on the left.',
    ] },
    { multi: false, title: 'When should a lob be played in padel?', options: [
      'Strictly when you are defending under extreme pressure.',
      'Strictly when you have total control of the ball and zero opponent pressure.',
      'Both: defensively (to recover time) and offensively (opponents tight to net to steal position).',
      'Never, because any lob gives the opponents an automatic winning smash.',
    ] },
    { multi: true, title: 'What defines a "chiquita" and how must it be followed tactically?', options: [
      'A slow, soft ball placed low directly into the feet of opponents at the net.',
      'It forces opponents to volley upward, creating an attackable ball on the next shot.',
      'After hitting it with good depth, you and your partner advance forward to challenge the net.',
      "A flat shot at maximum velocity aimed directly at the opponent's chest.",
    ] },
    { multi: true, title: 'You are at the net and accidentally hit a short, high lob. What is the correct reaction?', options: [
      'Stay glued to the net and step forward to try to block the smash.',
      'Retreat rapidly toward the defensive baseline zone to prepare for the smash.',
      "Read the opponent's hitting angle and prepare to defend the rebound off the back glass.",
      'Freeze in the transition zone (service line / "T") watching where the ball bounces.',
    ] },
    { multi: true, title: 'How should body posture and contact point be managed on a defensive lob from the back?', options: [
      'Contact slightly forward but close to the body, low stable center of gravity, lifting from shoulder/arm pendulum without excessive body extension.',
      'Contact in front of the body, generating height primarily through active leg flexion and extension (pushing up with the legs).',
      'Contact far behind the body, relying entirely on a fast wrist snap.',
      'Contact high above shoulders while jumping backward with both feet.',
    ] },
    { multi: false, title: 'What is the standard, most reliable forehand groundstroke from the baseline in padel?', options: [
      'Heavy topspin with aggressive racket acceleration like in clay-court tennis.',
      'Compact, flat stroke or slight backspin (slice) for control and a low rebound off the glass.',
      'Always a high, slow slice floater aimed at the opposite back corner.',
      'Aggressive sidespin flick on every single baseline drive.',
    ] },
    { multi: true, title: 'How should you approach and execute an aggressive back glass defense (bajada de pared)?', options: [
      "Assess opponents' net distance before striking: if glued to the net, play a sliced drive to feet/body or a chiquita; if waiting deep, hit a flat, fast ball down the center.",
      'Prepare racket high above shoulders, let ball reach its peak off the glass, and hit down hard with flat power to try to win the point directly.',
      "Hit with topspin while falling backward so the ball bounces very high off the opponent's glass.",
      'Stand on the service line and strike the ball before it touches the glass to avoid wall play.',
    ] },
    { multi: false, title: 'After hitting your serve, what is your immediate priority?', options: [
      "Stay at the baseline and check whether the opponent's return is weak.",
      'Advance forward immediately after contact to establish the net position with your partner.',
      'Run to the transition zone ("T" line) and pause there to observe the point.',
      'Switch sides immediately with your partner while the ball crosses the net.',
    ] },
    { multi: true, title: 'Opponents hit a deep, well-placed lob over your head while you are at the net. How do you react?', options: [
      'Pivot sideways with crossover steps; if unable to hit an overhead with balance, call it early, rotate or drop back in sync, and defend off the back glass.',
      'Run straight backward facing the net as fast as possible until reaching the back glass, stopping before the bounce.',
      'Stay at the net and attempt to jump backward to touch the ball with the racket tip.',
      'Stay at the net while your partner runs back alone to defend your side.',
    ] },
    { multi: true, title: 'What is the technical and tactical difference between a bandeja and a víbora?', options: [
      'Bandeja prioritizes control, safety, and depth to keep the net without taking excessive risks.',
      'Víbora is an aggressive overhead with heavy sidespin that keeps the ball fast and low off the glass.',
      'Bandeja is prepared with open racket face in front/above; víbora is struck beside the head with wrist brush.',
      'There is no technical difference; the naming only depends on whether the player is left- or right-handed.',
    ] },
  ],
};
const QN = QUIZ.de.length;

const TIER_STYLE = {
  green: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'yellow-high': 'bg-amber-100 text-amber-800 border-amber-300',
};

const RESULT_COPY = {
  de: {
    heading: 'Dein empfohlenes Start-Level',
    footer: 'Dein Level ist jetzt gespeichert — aber noch nicht verifiziert.',
    footerKnown: 'Dein Level ist jetzt gespeichert — aber noch nicht verifiziert.',
    verifyTitle: 'Werde verifiziert & Mitglied',
    verifyBody: 'Buche eine (kostenpflichtige) Level-Test-Session mit einem unserer Trainer. Dein Level wird auf dem Court verifiziert und die Mitgliedschaft ist im Preis inklusive:',
    perkBlueTick: 'Blauer Verifizierungs-Haken auf deinem Profil',
    perkMember: 'Mitgliedschaft inklusive (im Level-Test enthalten)',
    perkMatches: 'Priorität bei Open Matches & Waitlist plus Mitglieder-Rabatte',
    verifyCta: 'Level-Test buchen',
    goDashboard: 'Weiter zum Dashboard',
    retake: 'Level neu bestimmen',
  },
  en: {
    heading: 'Your recommended starting level',
    footer: 'Your level is saved — but not yet verified.',
    footerKnown: 'Your level is saved — but not yet verified.',
    verifyTitle: 'Get verified & become a member',
    verifyBody: 'Book a (paid) level-test session with one of our trainers. Your level is verified on-court and the membership is included in the price:',
    perkBlueTick: 'Blue verification tick on your profile',
    perkMember: 'Membership included (part of the level-test)',
    perkMatches: 'Priority on Open Matches & waitlist plus member discounts',
    verifyCta: 'Book level test',
    goDashboard: 'Continue to dashboard',
    retake: 'Change my level',
  },
};

const INTRO_COPY = {
  de: {
    title: 'Bevor du startest',
    honesty: 'Bitte fülle dies so ehrlich wie möglich aus. Das Level bestimmt, mit welchen Spielern du gematcht wirst und welche Trainings dir vorgeschlagen werden — falsche Angaben führen zu unpassenden Partien für alle.',
    playtomicTitle: 'Achtung: Unsere Skala ist NICHT dieselbe wie Playtomic',
    playtomicBody: 'Bei uns bedeutet eine tiefere Zahl ein höheres Level — die Skala ist umgekehrt zu Playtomic. Wenn du dein Playtomic-Level kennst, benutze diese Tabelle als Richtwert (im Zweifel lieber das Quiz starten):',
    playtomicNote: 'Nicht sicher? Lass dein Level lieber im Level-Test auf dem Court von einem Trainer bestimmen — dann stimmt es garantiert.',
    verifyTitle: 'So wirst du offiziell verifiziert',
    verifyBody: 'Möchtest du deinen Level-Status auf dem Court bestätigen lassen? Buche eine',
    verifyLink: 'Level-Test-Session',
    verifyBody2: '— dein Level wird von einem Trainer verifiziert und die Mitgliedschaft mit blauem Haken, Rabatten und weiteren Perks ist im Preis inklusive.',
    knownTitle: 'Du kennst dein Level bereits?',
    knownSub: 'Wenn du bei Padelarena Wädenswil schon einen Level-Test absolviert hast, kannst du deine Zahl direkt eintragen — kein Quiz nötig.',
    knownPlaceholder: 'z.B. 3.5',
    knownSubmit: 'Level speichern',
    startQuizTitle: 'Du kennst dein Level nicht?',
    startQuizSub: '10 Fragen zu Taktik & Technik — wir schlagen dir ein Start-Level vor (max. 4.0, für Orange ist der Level-Test auf dem Platz nötig).',
    startQuizBtn: 'Quiz starten',
    unsure: 'Nicht sicher? Schreib uns an ',
    later: 'Später erledigen',
    range: 'Zahl zwischen 1.0 (Profi) und 7.9 (Kompletter Anfänger), 0.5er Schritte',
  },
  en: {
    title: 'Before you start',
    honesty: 'Please answer as honestly as possible. Your level decides who we match you with and which lessons we recommend — wrong answers lead to unfair games for everyone.',
    playtomicTitle: 'Heads up: our scale is NOT the same as Playtomic',
    playtomicBody: 'On our scale a lower number means a higher level — the opposite of Playtomic. If you know your Playtomic level, use the table below as a rough conversion (when in doubt take the quiz):',
    playtomicNote: 'Not sure? Get your level verified on-court by a trainer — that way it is guaranteed correct.',
    verifyTitle: 'How to get officially verified',
    verifyBody: 'Want your level confirmed on-court? Book a',
    verifyLink: 'level-test session',
    verifyBody2: '— a trainer verifies your level and the membership with blue tick, discounts and other perks is included in the price.',
    knownTitle: 'Already know your level?',
    knownSub: "If you've done an on-court level test at Padelarena Wädenswil, just enter the number — no quiz needed.",
    knownPlaceholder: 'e.g. 3.5',
    knownSubmit: 'Save level',
    startQuizTitle: "Don't know your level?",
    startQuizSub: '10 questions on tactics & technique — we suggest a starting level (max. 4.0, Orange requires the on-court level test).',
    startQuizBtn: 'Start quiz',
    unsure: 'Not sure? Reach out at ',
    later: 'Do this later',
    range: 'Number between 1.0 (Pro) and 7.9 (Complete Beginner), 0.5 steps',
  },
};

// Approximate Playtomic → Padelarena mapping. Playtomic goes 0 (beginner) → 7
// (pro) with higher = better; ours is inverted (1.0 pro → 7.9 beginner).
const PLAYTOMIC_MAPPING = [
  { playtomic: '0.0 – 1.0', padelarena: '7.0 – 7.9', labelDe: 'Absoluter Anfänger', labelEn: 'Complete beginner' },
  { playtomic: '1.5 – 2.0', padelarena: '6.0 – 6.5', labelDe: 'Anfänger', labelEn: 'Beginner' },
  { playtomic: '2.5 – 3.0', padelarena: '5.0 – 5.5', labelDe: 'Fortgeschr. Anfänger', labelEn: 'Adv. beginner' },
  { playtomic: '3.5 – 4.0', padelarena: '4.0 – 4.5', labelDe: 'Mittelstufe', labelEn: 'Intermediate' },
  { playtomic: '4.5 – 5.0', padelarena: '3.0 – 3.5', labelDe: 'Fortgeschritten', labelEn: 'Advanced' },
  { playtomic: '5.5 – 6.0', padelarena: '2.0 – 2.5', labelDe: 'Turnier / Liga', labelEn: 'Tournament / league' },
  { playtomic: '6.5 – 7.0', padelarena: '1.0 – 1.5', labelDe: 'Profi', labelEn: 'Pro' },
];

const CLUB_EMAIL = 'info@padelarena.ch';

const levelBadge = (level, lang) => getLevelLabel(level, lang);

const LevelQuiz = () => {
  const navigate = useNavigate();
  const { user, language, API, checkAuth } = useAuth();
  const lang = language === 'de' ? 'de' : 'en';
  const questions = QUIZ[lang];

  // Lock-out guard (Oct 2026): once a user has completed the quiz, only
  // admin/coaches can change their level. Direct URL access is bounced
  // back to the profile with an explanation.
  const [mode, setMode] = useState('intro');    // 'intro' | 'quiz' | 'result'
  React.useEffect(() => {
    if (mode !== 'result' && (user?.level_quiz_completed_at || user?.is_member)) {
      navigate('/profile', { replace: true });
    }
  }, [user, navigate, mode]);

  const [step, setStep] = useState(0);          // 0..4 questions
  const [answers, setAnswers] = useState(() => Array.from({ length: QN }, () => []));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [knownLevel, setKnownLevel] = useState('');

  const pickAnswer = (idx) => {
    const next = [...answers];
    if (questions[step].multi) {
      const cur = next[step];
      next[step] = cur.includes(idx) ? cur.filter(i => i !== idx) : [...cur, idx].sort();
    } else {
      next[step] = [idx];
      if (step < QN - 1) setTimeout(() => setStep(step + 1), 180);
    }
    setAnswers(next);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await API.post('/auth/level-quiz', { answers });
      setResult(data);
      setMode('result');
      await checkAuth(); // refresh user in auth context
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'de' ? 'Konnte Quiz nicht senden' : 'Failed to submit quiz'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitKnownLevel = async () => {
    const parsed = parseFloat(knownLevel.replace(',', '.'));
    if (isNaN(parsed) || parsed < LEVEL_MIN || parsed > LEVEL_MAX) {
      toast.error(lang === 'de' ? `Bitte gib eine Zahl zwischen ${LEVEL_MIN} und ${LEVEL_MAX} ein` : `Please enter a number between ${LEVEL_MIN} and ${LEVEL_MAX}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post('/auth/known-level', { level: parsed });
      setResult({ ...data, source: 'self_reported_test' });
      setMode('result');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'de' ? 'Konnte Level nicht speichern' : 'Could not save level'));
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setAnswers(Array.from({ length: QN }, () => []));
    setStep(0);
    setResult(null);
    setKnownLevel('');
    setMode('intro');
  };

  const progress = mode === 'result' ? 100 : (step / QN) * 100;
  const currentAnswer = answers[step];

  // ---------- Result view ----------
  if (mode === 'result' && result) {
    const copy = RESULT_COPY[lang];
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg card-static p-8 md:p-10 text-center"
          data-testid="level-quiz-result"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-6"
          >
            <Trophy size={44} weight="fill" className="text-emerald-600" />
          </motion.div>
          <p className="text-sm uppercase tracking-wider text-emerald-700 font-bold mb-2">
            {copy.heading}
          </p>
          <p className="text-6xl font-black text-slate-900 mb-2" data-testid="level-quiz-level">
            {Number(result.player_level).toFixed(2)}
          </p>
          <p className="text-lg font-semibold text-slate-700 mb-3">
            {levelBadge(result.player_level, lang)}
          </p>
          {result.tier_de && (
            <span className={`inline-block border rounded-full px-3 py-1 text-sm font-bold mb-6 ${TIER_STYLE[result.tier_color] || TIER_STYLE.green}`} data-testid="level-quiz-tier">
              {lang === 'de' ? result.tier_de : result.tier_en}
            </span>
          )}
          <p className="text-xs text-slate-500 mb-6 leading-relaxed">
            {result.source === 'self_reported_test' ? copy.footerKnown : copy.footer}
          </p>

          {/* Get verified promo */}
          <div
            className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4 mb-6 text-left"
            data-testid="level-quiz-result-verify-promo"
          >
            <div className="flex items-center gap-2 mb-2">
              <SealCheck size={22} weight="fill" className="text-blue-600 flex-shrink-0" />
              <p className="font-bold text-blue-900">{copy.verifyTitle}</p>
            </div>
            <p className="text-sm text-slate-700 mb-3 leading-relaxed">{copy.verifyBody}</p>
            <ul className="text-sm text-slate-700 space-y-1.5 mb-3">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">✓</span>
                <span>{copy.perkBlueTick}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">✓</span>
                <span>{copy.perkMember}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">✓</span>
                <span>{copy.perkMatches}</span>
              </li>
            </ul>
            <Button
              onClick={() => {
                // No online booking for the level-test — a trainer must
                // meet the player on court to verify their level. Open
                // WhatsApp directly with a pre-filled message so the front-
                // desk can schedule and take payment offline.
                const displayedLevel = Number(result.player_level).toFixed(2);
                const msgDe = `Hallo Padelarena Wädenswil, ich möchte einen Level-Test buchen. Mein Quiz-Level: ${displayedLevel}. Wann hättet ihr Zeit?`;
                const msgEn = `Hi Padelarena Wädenswil, I'd like to book a level-test. My quiz level: ${displayedLevel}. When are you available?`;
                const text = encodeURIComponent(lang === 'de' ? msgDe : msgEn);
                window.open(`https://wa.me/41799160834?text=${text}`, '_blank', 'noopener');
              }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="level-quiz-result-verify-cta"
            >
              <SealCheck size={16} weight="fill" className="mr-2" />
              {copy.verifyCta}
            </Button>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed text-center">
              {lang === 'de'
                ? 'Kein Online-Buchen — ein Trainer verifiziert dein Level auf dem Court. Schreib uns per WhatsApp 079 916 0834 und wir vereinbaren einen Termin.'
                : 'No online booking — a trainer verifies your level on court. Message us on WhatsApp 079 916 0834 to schedule a slot.'}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button
              // Hard navigation (window.location instead of react-router's
              // navigate) because the /dashboard ProtectedRoute guard reads
              // `user.level_quiz_completed_at`, and the SPA setState from
              // `checkAuth()` may not have flushed yet when the user clicks.
              // A full reload boots the app fresh with the updated user
              // context, so the guard sees the completion timestamp and
              // routes correctly to /dashboard instead of bouncing back
              // to the quiz.
              onClick={() => { window.location.assign('/dashboard'); }}
              className="btn-primary w-full"
              data-testid="level-quiz-finish"
            >
              {copy.goDashboard}
            </Button>
            <button
              onClick={retake}
              className="text-sm text-slate-500 hover:text-slate-900 underline"
              data-testid="level-quiz-retake"
            >
              {copy.retake}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------- Intro view ----------
  if (mode === 'intro') {
    const copy = INTRO_COPY[lang];
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 pb-32 md:pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-xl"
        >
          <div className="card-static p-8 md:p-10" data-testid="level-quiz-intro">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-emerald-700 mb-3">
                <Sparkle size={18} weight="fill" />
                <span className="text-xs uppercase tracking-wider font-bold">
                  {lang === 'de' ? 'Level-Check' : 'Level Check'}
                </span>
              </div>
              <h1 className="text-2xl font-black text-slate-900">{copy.title}</h1>
            </div>

            {/* Honesty notice */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mb-4 flex gap-3" data-testid="level-quiz-honesty">
              <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 leading-relaxed">{copy.honesty}</p>
            </div>

            {/* Playtomic scale warning + mapping table */}
            <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-4 mb-4" data-testid="level-quiz-playtomic-warning">
              <div className="flex gap-3 mb-3">
                <Warning size={20} weight="fill" className="text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-rose-900 mb-1">{copy.playtomicTitle}</p>
                  <p className="text-sm text-rose-900/90 leading-relaxed">{copy.playtomicBody}</p>
                </div>
              </div>
              <div className="overflow-hidden rounded-md border border-rose-200 bg-white">
                <table className="w-full text-xs" data-testid="level-quiz-playtomic-table">
                  <thead>
                    <tr className="bg-rose-100/60 text-rose-900">
                      <th className="text-left px-3 py-2 font-bold">Playtomic</th>
                      <th className="text-left px-3 py-2 font-bold">{lang === 'de' ? 'Padelarena' : 'Padelarena'}</th>
                      <th className="text-left px-3 py-2 font-bold hidden sm:table-cell">{lang === 'de' ? 'Beschreibung' : 'Description'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PLAYTOMIC_MAPPING.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-rose-50/40'}>
                        <td className="px-3 py-1.5 text-slate-600">{row.playtomic}</td>
                        <td className="px-3 py-1.5 font-bold text-slate-900">{row.padelarena}</td>
                        <td className="px-3 py-1.5 text-slate-500 hidden sm:table-cell">{lang === 'de' ? row.labelDe : row.labelEn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-rose-800/80 mt-2 leading-relaxed">{copy.playtomicNote}</p>
            </div>

            {/* Get verified promo */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4 mb-6 flex gap-3" data-testid="level-quiz-verify-promo">
              <SealCheck size={22} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-800 leading-relaxed">
                <p className="font-bold text-blue-900 mb-1">{copy.verifyTitle}</p>
                <p>
                  {copy.verifyBody}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      const msgDe = 'Hallo Padelarena Wädenswil, ich möchte einen Level-Test buchen. Wann hättet ihr Zeit?';
                      const msgEn = 'Hi Padelarena Wädenswil, I would like to book a level-test. When are you available?';
                      const text = encodeURIComponent(lang === 'de' ? msgDe : msgEn);
                      window.open(`https://wa.me/41799160834?text=${text}`, '_blank', 'noopener');
                    }}
                    className="text-blue-700 font-bold underline hover:text-blue-800"
                    data-testid="level-quiz-verify-intro-link"
                  >
                    {copy.verifyLink}
                  </button>
                  {' '}{copy.verifyBody2}
                </p>
              </div>
            </div>

            {/* Known level shortcut */}
            <div className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-1">{copy.knownTitle}</h3>
              <p className="text-xs text-slate-600 mb-3">{copy.knownSub}</p>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="1"
                    max="7.9"
                    inputMode="decimal"
                    value={knownLevel}
                    onChange={(e) => setKnownLevel(e.target.value)}
                    placeholder={copy.knownPlaceholder}
                    className="bg-white border-emerald-300 text-slate-900 font-bold text-lg"
                    data-testid="level-quiz-known-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">{copy.range}</p>
                </div>
                <Button
                  onClick={submitKnownLevel}
                  disabled={submitting || !knownLevel}
                  className="btn-primary"
                  data-testid="level-quiz-known-submit"
                >
                  {copy.knownSubmit}
                </Button>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-semibold uppercase">
                {lang === 'de' ? 'oder' : 'or'}
              </span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Start quiz */}
            <div className="border-2 border-slate-200 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-1">{copy.startQuizTitle}</h3>
              <p className="text-xs text-slate-600 mb-3">{copy.startQuizSub}</p>
              <Button
                onClick={() => setMode('quiz')}
                variant="outline"
                className="w-full border-2 border-slate-300 font-bold"
                data-testid="level-quiz-start-quiz"
              >
                {copy.startQuizBtn}
                <CaretRight size={16} className="ml-1" />
              </Button>
            </div>

            {/* Contact fallback */}
            <div className="text-center text-xs text-slate-500 flex items-center justify-center gap-1 flex-wrap">
              <ChatCircleText size={14} className="text-slate-400" />
              <span>{copy.unsure}</span>
              <a
                href={`mailto:${CLUB_EMAIL}?subject=${encodeURIComponent(lang === 'de' ? 'Frage zu meinem Padel-Level' : 'Question about my padel level')}`}
                className="text-emerald-700 font-semibold underline hover:text-emerald-800"
                data-testid="level-quiz-contact-email"
              >
                {CLUB_EMAIL}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------- Question view ----------
  const q = questions[step];
  const allAnswered = answers.every((a) => a.length > 0);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl"
      >
        <div className="card-static p-8 md:p-10" data-testid="level-quiz">
          {/* Header */}
          <div className="mb-6 text-center">
            <div className="inline-flex items-center gap-2 text-emerald-700 mb-3">
              <Sparkle size={18} weight="fill" />
              <span className="text-xs uppercase tracking-wider font-bold">
                {lang === 'de' ? 'Level-Check' : 'Level Check'}
              </span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-1">
              {lang === 'de' ? 'Finde dein Padel-Level' : 'Find your padel level'}
            </h1>
            <p className="text-sm text-slate-500">
              {lang === 'de'
                ? '10 Fragen zu Taktik & Technik. Falsche Antworten geben Abzug — kreuze nur an, was du sicher weisst.'
                : '10 questions on tactics & technique. Wrong answers cost points — only tick what you are sure about.'}
            </p>
          </div>

          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-slate-500 mb-2 font-semibold">
              <span>{lang === 'de' ? 'Frage' : 'Question'} {step + 1} / {QN}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-emerald-500"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Question */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="text-lg font-bold text-slate-900 mb-2">{q.title}</h2>
              <p className="text-xs font-semibold text-slate-500 mb-4" data-testid="level-quiz-answer-hint">
                {q.multi
                  ? (lang === 'de' ? 'Mehrfachauswahl möglich' : 'Multiple answers possible')
                  : (lang === 'de' ? 'Eine Antwort' : 'One answer')}
              </p>
              <div className="space-y-2">
                {q.options.map((opt, idx) => {
                  const sel = currentAnswer.includes(idx);
                  return (
                    <button
                      key={idx}
                      onClick={() => pickAnswer(idx)}
                      data-testid={`level-quiz-q${step}-option-${idx}`}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm flex items-start ${
                        sel ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      <span className={`inline-flex shrink-0 w-6 h-6 ${q.multi ? 'rounded-md' : 'rounded-full'} text-xs font-bold mr-3 items-center justify-center ${sel ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                        {q.multi && sel ? '✓' : String.fromCharCode(65 + idx)}
                      </span>
                      <span>{opt}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="text-slate-600"
              data-testid="level-quiz-prev"
            >
              <CaretLeft size={16} className="mr-1" />
              {lang === 'de' ? 'Zurück' : 'Back'}
            </Button>

            {step < QN - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={currentAnswer.length === 0}
                className="btn-primary"
                data-testid="level-quiz-next"
              >
                {lang === 'de' ? 'Weiter' : 'Next'}
                <CaretRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={!allAnswered || submitting}
                className="btn-primary"
                data-testid="level-quiz-submit"
              >
                {submitting
                  ? lang === 'de' ? 'Wird gesendet…' : 'Submitting…'
                  : lang === 'de' ? 'Auswerten' : 'See result'}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LevelQuiz;
